import { IS_APPLE } from "~/lib/environment"

export const modifierKey = IS_APPLE ? "Cmd" : "Ctrl"

export const appShortcuts = {
	open_search: {
		title: "Open Search",
		description: "Open the command palette and global search.",
		keys: ["Mod", "K"]
	},
	open_keyboard_shortcuts: {
		title: "Open Keyboard Shortcuts",
		description: "Show available shortcuts and quick navigation.",
		keys: ["Mod", "/"]
	},
	toggle_sidebar: {
		title: "Toggle Sidebar",
		description: "Collapse or expand the main sidebar.",
		keys: ["Mod", "B"]
	},
	submit_comment: {
		title: "Post Comment",
		description: "Post the focused comment or reply.",
		keys: ["Mod", "Enter"]
	},
	submit_issue: {
		title: "Submit Issue",
		description: "Create the issue from the create issue dialog.",
		keys: ["Mod", "Enter"]
	}
} as const

export const quickNavigationShortcuts = {
	create_issue: {
		title: "Create Issue",
		description: "Open the create issue dialog.",
		keys: ["C"],
		keywords: ["new", "ticket", "bug", "task"]
	},
	create_knowledge_base_page: {
		title: "Create Knowledge Base Page",
		description: "Create a new page in the current team's knowledge base.",
		keys: ["N", "K"],
		keywords: ["new", "doc", "document", "kb"]
	},
	go_home: {
		title: "Organization Home",
		description: "Navigate to the current organization home.",
		keys: ["O", "H"],
		keywords: ["org", "organization", "home"]
	},
	open_inbox: {
		title: "My Inbox",
		description: "Navigate to your inbox.",
		keys: ["I"],
		keywords: ["notifications", "mentions"]
	},
	open_my_issues: {
		title: "My Issues",
		description: "Navigate to issues assigned to you.",
		keys: ["M", "I"],
		keywords: ["assigned", "issues", "me"]
	},
	open_user_settings: {
		title: "User Settings",
		description: "Navigate to your user settings.",
		keys: ["U", "S"],
		keywords: ["profile", "account", "me"]
	},
	open_organization_settings: {
		title: "Organization Settings",
		description: "Navigate to the current organization settings.",
		keys: ["O", "S"],
		keywords: ["org", "members", "roles"]
	},
	open_team_issues: {
		title: "Team Issues",
		description: "Navigate to issues for the current team.",
		keys: ["T", "I"],
		keywords: ["current", "team", "board", "list"]
	},
	open_team_queue: {
		title: "Team Queue",
		description: "Navigate to the current team's queue.",
		keys: ["T", "Q"],
		keywords: ["current", "team", "triage"]
	},
	open_knowledge_base: {
		title: "Knowledge Base",
		description: "Navigate to the current team's knowledge base.",
		keys: ["K"],
		keywords: ["docs", "documents", "kb", "team"]
	},
	open_team_settings: {
		title: "Team Settings",
		description: "Navigate to the current team settings.",
		keys: ["T", "S"],
		keywords: ["current", "team", "members", "labels"]
	}
} as const

export type QuickNavigationShortcutId = keyof typeof quickNavigationShortcuts

export function displayShortcut(keys: readonly string[]) {
	return keys.map((key) => (key === "Mod" ? modifierKey : key)).join(" ")
}

export function shortcutSequenceMatches(
	keys: readonly string[],
	sequence: readonly string[]
) {
	return displayShortcut(keys) === sequence.join(" ")
}

export function shortcutSequenceStartsWith(
	keys: readonly string[],
	sequence: readonly string[]
) {
	return displayShortcut(keys).startsWith(`${sequence.join(" ")} `)
}

export function isModifiedShortcut(
	event: KeyboardEvent,
	keys: readonly string[],
	modifierActive = false
) {
	if (keys.length !== 2 || keys[0] !== "Mod") return false
	if (!modifierActive && !event.metaKey && !event.ctrlKey) return false

	const key = keys[1]
	if (!key) return false
	if (key === "/") return event.key === "/" || event.code === "Slash"

	return event.key.toLowerCase() === key.toLowerCase()
}

export function firstShortcutKey(keys: readonly string[]) {
	return keys[0]?.toLowerCase()
}
